import { useMemo } from 'react';

import { PasswordValidationRule } from 'api/types/data';
import { HiddenBoxStyled } from 'components';
import { PasswordValidation } from 'helpers';
import { Form } from 'libs/form';
import { Submit } from 'libs/form/Submit';
import { useTranslation } from 'libs/i18n';
import { Stack, Typography } from 'libs/ui';
import { AuthView } from 'modules/auth/common/views/AuthView';

import { PasswordRules, TwoFactorAuthentication } from '../components';
import {
  Field,
  getSchema,
  FormSubmit,
  FormErrors,
} from '../forms/passwordForm';

interface Props {
  passwordValidationRule?: PasswordValidationRule;
  formErrors?: FormErrors;
  submitLoading?: boolean;
  submitLabel?: string;
  isTwoFactorAuthentication?: boolean;
  title: string;
  subtitle: string;
  passwordLabel?: string;
  onSubmit: FormSubmit;
  onBackPassword?: () => void;
}

export const SetPasswordView = ({
  passwordValidationRule,
  formErrors,
  submitLoading,
  submitLabel,
  isTwoFactorAuthentication = false,
  title,
  subtitle,
  passwordLabel,
  onSubmit,
  onBackPassword,
}: Props) => {
  const { t } = useTranslation();

  const passwordValidation = useMemo(
    () => PasswordValidation.parseByValidationRule(passwordValidationRule),
    [passwordValidationRule]
  );

  const schema = useMemo(
    () => getSchema(t, passwordValidation),
    [passwordValidation, t]
  );

  return (
    <AuthView>
      <Form
        initialErrors={formErrors}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        <HiddenBoxStyled hidden={isTwoFactorAuthentication}>
          <Stack spacing={32}>
            <Stack spacing={16}>
              <Typography className="formContainer-title" variant="headerBold">
                {title}
              </Typography>
              <Typography variant="bodyMedium">{subtitle}</Typography>
            </Stack>
            <Field.Password
              allowSubmit
              fullWidth
              autoComplete="new-password"
              label={passwordLabel}
              name="password"
              placeholder={t('auth.setPassword.placeholder')}
            />
            <PasswordRules
              passwordFieldName="password"
              passwordValidation={passwordValidation}
            />

            <Submit
              fullWidth
              label={submitLabel ?? t('common.next')}
              loading={submitLoading}
            />
          </Stack>
        </HiddenBoxStyled>

        {isTwoFactorAuthentication && (
          <TwoFactorAuthentication onBackPassword={onBackPassword} />
        )}
      </Form>
    </AuthView>
  );
};
